import React from "react";
import "../CSS/ContentPage.css";

const RefundPolicy = () => {
  return (
    <>
      <title>Refund & Cancellation Policy | Fair & Transparent - Syncnode IT Services</title>
      <meta name="description" content="Syncnode's refund and cancellation policy for IT services. Refund eligibility within 7-14 days, transparent process, milestone-based project cancellations. Fair refund guidelines for software projects." />
      <meta name="keywords" content="refund policy, cancellation policy, money back guarantee, software project cancellation, IT services refund, web development refund, Syncnode refund" />
      <link rel="canonical" href="https://www.syncnode.in/refundpolicy" />
      <meta property="og:title" content="Refund & Cancellation Policy | Fair & Transparent - Syncnode" />
      <meta property="og:description" content="Transparent refund and cancellation policy for IT services. Clear guidelines for refund eligibility, timeframes, and process." />
      <meta property="og:url" content="https://www.syncnode.in/refundpolicy" />
      <meta property="og:type" content="website" />
      <div className="contentpage_wrapper">
        <div className="container">
          <div className="content_banner">
            <h1>
              Refund & Cancellation{" "}
              <span className="decorated_text">Policy</span>
            </h1>
            <p className="intro_text">
              We strive for customer satisfaction. Learn about our refund
              eligibility, timeframes, and cancellation process.
            </p>

            <div className="last_updated">
              <p>Last Updated: October 31, 2025</p>
            </div>
          </div>
          <div className="main_content">
            <h2 className="section_heading">Introduction</h2>
            <p className="content_para">
              At Syncnode, we are committed to providing high-quality digital
              solutions including Web Development, Mobile Development,
              Automation, E-commerce, Digital Marketing, and Tech Consulting
              services that meet your needs and expectations. However, we
              understand that there may be instances where a refund or
              cancellation is necessary. This policy outlines our guidelines for
              refund and cancellation requests to ensure a fair and transparent
              process for our valued clients.
            </p>

            <h2 className="section_heading">
              Refund Eligibility and Timeframes
            </h2>
            <p className="content_para">
              Our refund policy applies to paid services and subscriptions. To
              be eligible for a refund, you must request it within the specified
              timeframes outlined below. After these periods, refunds will not
              be issued.
            </p>

            <h5 className="section_sub_heading">Refundable Services</h5>
            <ul>
              <li>
                <strong>Subscription Services:</strong> Monthly and annual
                subscription fees (within 14 days of purchase)
              </li>
              <li>
                <strong>One-time Development Services:</strong> Custom
                development projects (within 7 days, before work commences)
              </li>
              <li>
                <strong>Setup and Configuration Fees:</strong> Initial setup
                charges (within 7 days of payment)
              </li>
              <li>
                <strong>Digital Marketing Packages:</strong> Marketing service
                packages (within 7 days, before campaign launch)
              </li>
              <li>
                <strong>Consulting Services:</strong> Pre-paid consulting
                packages (within 7 days, before sessions begin)
              </li>
            </ul>

            <h5 className="section_sub_heading">Non-Refundable Services</h5>
            <ul>
              <li>
                <strong>Completed Development Work:</strong> Any custom
                development work that has been delivered and accepted by the
                client
              </li>
              <li>
                <strong>Third-party Licenses:</strong> Software licenses,
                plugins, or tools purchased through us from third-party vendors
              </li>
              <li>
                <strong>Domain and Hosting Fees:</strong> Expenses paid to
                external providers on your behalf
              </li>
              <li>
                <strong>Consultation Services Rendered:</strong> Time-based
                consulting and advisory services already provided
              </li>
              <li>
                <strong>Data Migration Services:</strong> Once data migration
                has been completed and verified
              </li>
              <li>
                <strong>Active Marketing Campaigns:</strong> Marketing campaigns
                that have already been launched or are in progress
              </li>
              <li>
                <strong>Training Services:</strong> Training sessions that have
                been conducted
              </li>
            </ul>

            <h2 className="section_heading">Grounds for Refunds</h2>
            <p className="content_para">
              We offer refunds in the following situations:
            </p>
            <ul>
              <li>
                <strong>Technical Issues:</strong> If our product or service
                fails to function as described or encounters significant
                technical issues that prevent its proper use within the
                eligibility timeframe, we will issue a full refund.
              </li>
              <li>
                <strong>Unmet Specifications:</strong> If you can demonstrate
                that our deliverable does not meet the features or
                functionalities clearly specified in the signed agreement or
                project scope document, a refund may be granted.
              </li>
              <li>
                <strong>Duplicate Payments:</strong> In the event of accidental
                duplicate payments, we will refund the duplicate charge upon
                verification.
              </li>
              <li>
                <strong>Service Unavailability:</strong> If we are unable to
                provide the contracted services due to circumstances within our
                control.
              </li>
              <li>
                <strong>Pre-commencement Cancellation:</strong> If you cancel
                before any work has begun within the specified timeframe.
              </li>
            </ul>
            <p className="content_para">
              <strong>Important:</strong> Refunds are not available for change
              of mind after work has commenced, lack of usage, or issues caused
              by third-party software or user error.
            </p>

            <h2 className="section_heading">Cancellation Policy</h2>

            <h5 className="section_sub_heading">Subscription Cancellations</h5>
            <ul>
              <li>
                <strong>Subscription Services:</strong> Can be cancelled at any
                time with 30 days written notice via email
              </li>
              <li>
                <strong>No Cancellation Fees:</strong> We do not charge
                cancellation fees for subscription services
              </li>
              <li>
                <strong>Access Termination:</strong> Access will be terminated
                at the end of the current paid billing period unless renewed
              </li>
              <li>
                <strong>Data Backup:</strong> You are responsible for backing up
                your data before cancellation. We retain data for 30 days
                post-cancellation.
              </li>
            </ul>

            <h5 className="section_sub_heading">
              Development Project Cancellations
            </h5>
            <ul>
              <li>
                <strong>Before Work Begins:</strong> Projects can be cancelled
                within 7 days of agreement signing with a full refund
              </li>
              <li>
                <strong>Work in Progress:</strong> If work has commenced,
                charges will apply for completed milestones plus a 25%
                cancellation fee on the remaining work budget
              </li>
              <li>
                <strong>Near Completion:</strong> Projects more than 75%
                complete cannot be cancelled, and full payment is due
              </li>
              <li>
                <strong>Deliverables:</strong> You will receive all completed
                work and assets up to the point of cancellation
              </li>
            </ul>

            <h5 className="section_sub_heading">
              Marketing Campaign Cancellations
            </h5>
            <ul>
              <li>
                <strong>Before Launch:</strong> Can be cancelled within 7 days
                with full refund
              </li>
              <li>
                <strong>Active Campaigns:</strong> Charges apply for ad spend
                and work completed. Remaining budget will be refunded minus
                platform fees and our management fee
              </li>
            </ul>

            <h2 className="section_heading">Refund Request Process</h2>
            <p className="content_para">To request a refund, you must:</p>
            <ul>
              <li>
                Contact our support team at <strong>hello@syncnode.in</strong>{" "}
                or call <strong>+91 7876666787</strong>
              </li>
              <li>
                Provide your order details, purchase date, and invoice number
              </li>
              <li>
                Include a detailed explanation of the reason for your refund
                request
              </li>
              <li>
                Submit any supporting documentation such as screenshots, error
                logs, or correspondence
              </li>
            </ul>
            <p className="content_para">
              Our support team will acknowledge receipt of your request within
              24 hours and provide a decision within 5 business days of
              receiving all required information.
            </p>

            <h2 className="section_heading">Refund Processing Timeline</h2>
            <p className="content_para">
              Once your refund is approved, the processing timeline is as
              follows:
            </p>
            <ul>
              <li>
                <strong>Approval Notification:</strong> Within 5 business days
                of your request
              </li>
              <li>
                <strong>Credit/Debit Cards:</strong> 7-10 business days for the
                refund to appear in your account
              </li>
              <li>
                <strong>Bank Transfer:</strong> 3-5 business days for direct
                bank transfers
              </li>
              <li>
                <strong>UPI/Digital Wallets:</strong> 1-3 business days
              </li>
              <li>
                <strong>International Payments:</strong> 10-15 business days
                depending on your bank and location
              </li>
            </ul>
            <p className="content_para">
              Approved refunds will be processed to the original payment method.
              If the original payment method is unavailable, alternative
              arrangements will be made in consultation with you.
            </p>

            <h2 className="section_heading">Damaged or Defective Services</h2>
            <p className="content_para">
              For digital services or deliverables that don't meet quality
              standards:
            </p>
            <ul>
              <li>
                Report issues within 7 days of service delivery or project
                completion
              </li>
              <li>
                We will investigate the issue and provide remedial action within
                3 business days
              </li>
              <li>
                If issues cannot be resolved to your satisfaction, a full or
                partial refund will be provided
              </li>
              <li>
                We may request detailed feedback and examples to improve our
                services
              </li>
            </ul>

            <h2 className="section_heading">Warranty and Third-Party Issues</h2>
            <ul>
              <li>
                <strong>Software Warranties:</strong> Issues with third-party
                software should be directed to the respective vendors. We
                provide integration support but cannot offer refunds for
                third-party product failures.
              </li>
              <li>
                <strong>Hardware Issues:</strong> Hardware-related problems are
                covered under manufacturer warranties, not our refund policy
              </li>
              <li>
                <strong>Integration Problems:</strong> We provide support for
                integration issues within our agreed scope of work only
              </li>
              <li>
                <strong>Platform Changes:</strong> Changes to third-party
                platforms (APIs, policies, features) beyond our control are not
                grounds for refund
              </li>
            </ul>

            <h2 className="section_heading">Exclusions</h2>
            <p className="content_para">
              The following scenarios are NOT eligible for refunds or
              cancellations:
            </p>
            <ul>
              <li>
                <strong>Change of Mind:</strong> Simple change of mind after the
                eligibility period has expired
              </li>
              <li>
                <strong>Lack of Usage:</strong> Not using the service or product
                after purchase
              </li>
              <li>
                <strong>Third-Party Compatibility:</strong> Issues arising from
                third-party software, platforms, or external factors beyond our
                control
              </li>
              <li>
                <strong>User Error:</strong> Problems caused by incorrect
                implementation, improper use, or failure to follow provided
                documentation
              </li>
              <li>
                <strong>Force Majeure:</strong> Delays or issues caused by
                events beyond our reasonable control including natural
                disasters, pandemics, or government actions
              </li>
              <li>
                <strong>Violation of Terms:</strong> Services terminated due to
                violation of our Terms of Service or acceptable use policy
              </li>
              <li>
                <strong>Incomplete Information:</strong> Delays or issues
                resulting from incomplete or inaccurate information provided by
                you
              </li>
              <li>
                <strong>Scope Changes:</strong> Additional costs incurred due to
                scope changes or feature additions requested after project
                commencement
              </li>
            </ul>

            <h2 className="section_heading">Partial Refunds</h2>
            <p className="content_para">
              In certain circumstances, partial refunds may be offered:
            </p>
            <ul>
              <li>
                For partially completed development projects where some
                milestones have been delivered
              </li>
              <li>
                When some services have been delivered but others have not due
                to circumstances within our control
              </li>
              <li>
                For pro-rated subscription cancellations in exceptional
                circumstances at our discretion
              </li>
              <li>
                When quality issues affect only a portion of the deliverables
              </li>
            </ul>
            <p className="content_para">
              Partial refund amounts will be calculated based on the value of
              work completed, resources utilized, and the stage of project
              completion.
            </p>

            <h2 className="section_heading">Dispute Resolution</h2>
            <p className="content_para">
              If you disagree with our refund decision:
            </p>
            <ul>
              <li>
                You may request a review by escalating to our management team
                via email at <strong>hello@syncnode.in</strong>
              </li>
              <li>
                Provide additional documentation or evidence supporting your
                case
              </li>
              <li>We will conduct a thorough review with fresh perspective</li>
              <li>
                Final decisions will be communicated within 10 business days of
                receiving your escalation
              </li>
              <li>
                If the dispute remains unresolved, both parties agree to attempt
                mediation before pursuing legal action
              </li>
            </ul>

            <h2 className="section_heading">Modifications to This Policy</h2>
            <p className="content_para">
              We reserve the right to modify this Refund and Cancellation Policy
              at any time. Changes will be effective immediately upon posting on
              our website with an updated "Last Updated" date. Your continued
              use of our services after such modifications constitutes
              acceptance of the updated policy. We encourage you to review this
              policy periodically.
            </p>

            <h2 className="section_heading">Contact Information</h2>
            <p className="content_para">
              For refund and cancellation requests, or if you have any questions
              about this policy, please contact us:
            </p>
            <div className="highlight_box">
              <ul>
                <li>
                  <strong>Email:</strong> info@syncnode.in
                </li>
                <li>
                  <strong>Phone:</strong> +91 7876666787
                </li>
                <li>
                  <strong>Address:</strong> Hisar, Haryana, India
                </li>
                <li>
                  <strong>Business Hours:</strong> 9:00 AM - 6:00 PM IST, Monday
                  to Friday
                </li>
              </ul>
            </div>
            <p className="content_para">
              We value your satisfaction and aim to address any concerns
              promptly and effectively. By providing clear guidelines for refund
              and cancellation requests, we ensure transparency and fairness in
              our business practices.
            </p>
            <div className="highlight_box">
              <p className="content_para">
                <strong>Note:</strong> This policy should be read in conjunction
                with our Terms and Conditions and Privacy Policy. In case of any
                conflict, the Terms and Conditions shall prevail.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundPolicy;
